import { createContext, useContext } from "react";
import type { IssueRow } from "#app/lib/data";
import type { IssueViewTab } from "./issue-view-types";
import type { Omittable } from "@salestrak/ui";

export type OpenIssueOptions = Omittable<{
  initialTab?: IssueViewTab;
  focusNoteId?: string | null;
  // When set, the summary tab scrolls to and briefly highlights this custom
  // field row (e.g. opening a paragraph-mention notification from the inbox).
  focusCustomFieldId?: string | null;
}>;

export type OpenCreateIssueOptions = Omittable<{
  workflowId?: string;
  stageId?: string;
  address?: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }>;
  name?: string;
  /** Where the create flow was launched from — included in issue create_start/create_finish analytics. */
  source?: string;
  onCreated?: (issue: IssueRow) => void;
}>;

export interface IssueViewContextValue {
  openIssue: (issue: IssueRow, options?: OpenIssueOptions) => void;
  openIssueById: (issueId: string, options?: OpenIssueOptions) => void;
  openCreateIssue: (options: OpenCreateIssueOptions) => void;
  closeIssue: () => void;
}

export const IssueViewContext = createContext<IssueViewContextValue | null>(null);

export function useIssueView() {
  const ctx = useContext(IssueViewContext);
  if (!ctx) throw new Error("useIssueView must be used within IssueViewProvider");
  return ctx;
}

export function useIssueViewOptional() {
  return useContext(IssueViewContext);
}
